import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import type { SupportedVendors } from '@eluve/graphql-types';
import { useTenantIdFromParams } from '@eluve/session-helpers';

type EhrOnboardingStatus = {
  isUserPartOfAnyLocation: boolean;
  areLocationsAvailable: boolean;
  hasEhrEnabled: boolean;
  hasWebBasedEhr: boolean;
  hasSyncedBefore: boolean;
  didUserFinishOnboarding: boolean;
  shouldPromptOnboarding: boolean;
  isExtensionRequired: boolean;
  externalEhrs: Array<{
    id: string;
    vendor: SupportedVendors;
    domain: string;
  }>;
  locations: Array<{
    id: string;
    name: string;
  }>;
};

const defaultStatus: EhrOnboardingStatus = {
  isUserPartOfAnyLocation: false,
  areLocationsAvailable: false,
  hasEhrEnabled: false,
  hasWebBasedEhr: false,
  hasSyncedBefore: false,
  didUserFinishOnboarding: false,
  shouldPromptOnboarding: false,
  isExtensionRequired: false,
  externalEhrs: [],
  locations: [],
};

export const useEhrOnboardingStatus = () => {
  const tenantId = useTenantIdFromParams();
  const apiClient = useApiClient();
  const [data, setData] = useState<EhrOnboardingStatus>(defaultStatus);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }
    try {
      const response = await apiClient.locations.getEhrOnboardingStatus({
        params: { tenantId },
      });
      if (response.status === 200) {
        setData(response.body);
      }
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, tenantId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { data, isLoading, refetch: fetchStatus };
};
